import React, { useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthProvider";
import OnboardingWidget from "./OnboardingWidget";
import FirstTimeUserModal from "./FirstTimeUserModal";
import OpsChatFirstTimeModal from "./OpsChatFirstTimeModal";

const OnboardingWidgetWrapper = () => {
  const { user, isOpsChatOnlyUser, isUserInfoLoading } = useContext(AuthContext);
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOtherModalOpen, setIsOtherModalOpen] = useState(false);

  // Hide onboarding widget on public Leo chat, tenant-chat routes, communication, etc.
  const isPublicLeoChatPage = location.pathname.startsWith("/chat/");
  const isTenantChatPage = location.pathname.startsWith("/tenant-chat");
  const isCommunicationPage =
    location.pathname === "/communication" ||
    location.pathname.startsWith("/communication/");
  const isTenantPaymentPage = location.pathname.startsWith("/pay-rent");
  const isPaymentSuccessPage = location.pathname === "/payment-success";
  const isVendorConversationPage =
    location.pathname.startsWith("/conversations/");
  const isListingPage = location.pathname.startsWith("/lease/");
  const isPublicApplyPage = location.pathname.startsWith("/apply/");
  const isTaxPrepPage = location.pathname === "/tax-prep";

  // Detect if any other modal is open by checking for ant-modal-mask elements
  // This includes first-time modals (FirstTimeUser / OpsChat) and this widget’s modal
  useEffect(() => {
    const checkModalOpen = () => {
      const modalMasks = document.querySelectorAll(".ant-modal-mask");
      // Hide widget if any modal is open (including FirstTimeUserModal or this widget's own modal)
      setIsOtherModalOpen(modalMasks.length > 0);
    };

    // Check immediately
    checkModalOpen();

    // Use MutationObserver to watch for modal changes
    const observer = new MutationObserver(checkModalOpen);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    // Also check periodically as a fallback
    const interval = setInterval(checkModalOpen, 100);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Wait until userInfo is loaded so isOpsChatOnlyUser is correct (avoids full-OS modal during load)
  // Only show the onboarding widget when user is logged in, not on excluded pages, and no modal is open
  // Hide when a first-time modal (wrapper) or this widget’s modal is open
  const shouldShow =
    user &&
    !isUserInfoLoading &&
    !isPublicLeoChatPage &&
    !isTenantChatPage &&
    !isCommunicationPage &&
    !isTenantPaymentPage &&
    !isPaymentSuccessPage &&
    !isVendorConversationPage &&
    !isListingPage &&
    !isPublicApplyPage &&
    !isTaxPrepPage &&
    !isOtherModalOpen &&
    !isModalOpen;

  return (
    <>
      {shouldShow && (
        <OnboardingWidget
          isVisible
          onOpenModal={handleOpenModal}
          variant={isOpsChatOnlyUser ? "opsChat" : "fullOs"}
        />
      )}
      {isOpsChatOnlyUser ? (
        <OpsChatFirstTimeModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      ) : (
        <FirstTimeUserModal isOpen={isModalOpen} onClose={handleCloseModal} />
      )}
    </>
  );
};

export default OnboardingWidgetWrapper;
